import type { DragEvent, ReactNode, Ref } from 'react';
import { Link } from 'react-router-dom';
import { cn, FileTree, type FileTreeEntry, type FileTreeRefreshHandle } from '@localagent/ui';
import type { FileBrowserNotice } from '../../hooks/useWorkspaceFileBrowser';

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0', spinning && 'animate-spin')}
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

/**
 * The directory-tree half of the split chat file browser. Renders the lazy tree
 * (or a loud not-ready notice), and turns a file selection into an "open tab"
 * via `onOpenFile`. State comes from `useWorkspaceFileBrowser` in the host so it
 * stays in sync with the separately-rendered tabbed viewer; the refresh
 * controller (button + `refreshRef`) is host-owned too — this stays
 * presentational.
 */
export function WorkspaceDirectoryTree({
  rootEntries,
  loadChildren,
  notReady,
  resume,
  activePath,
  onOpenFile,
  onNodeDragStart,
  onNodeContextMenu,
  onBackgroundContextMenu,
  onRefresh,
  refreshing,
  refreshError,
  actionError,
  refreshRef,
  headerActions,
}: {
  rootEntries: FileTreeEntry[] | null;
  loadChildren: (path: string) => Promise<FileTreeEntry[]>;
  notReady: FileBrowserNotice | null;
  resume: () => void | Promise<unknown>;
  activePath: string | null;
  onOpenFile: (path: string) => void;
  onNodeDragStart?: (entry: FileTreeEntry, e: DragEvent) => void;
  onNodeContextMenu?: (entry: FileTreeEntry, pos: { x: number; y: number }) => void;
  onBackgroundContextMenu?: (pos: { x: number; y: number }) => void;
  /** Manual-refresh handler (host applies `{ manual: true }` semantics). */
  onRefresh: () => void;
  refreshing: boolean;
  /** Last refresh failure, surfaced in the header notice slot (fail-visible). */
  refreshError: string | null;
  /** Last context-menu action failure, surfaced like `refreshError`. */
  actionError?: string | null;
  /** Imperative tree-refresh handle, threaded to the FileTree for the host's
   *  refresh controller. */
  refreshRef?: Ref<FileTreeRefreshHandle>;
  headerActions?: ReactNode;
}) {
  const header = (
    <div className="flex flex-shrink-0 items-center justify-between gap-1 px-2 pt-2">
      <span className="text-caption font-medium text-muted-foreground">Files</span>
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          data-testid="refresh-file-tree"
          aria-label="Refresh files"
          title="Refresh files"
          disabled={refreshing || notReady?.tone === 'warning'}
          onClick={onRefresh}
          className="rounded-sm p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          <RefreshIcon spinning={refreshing} />
        </button>
        {headerActions}
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col" data-testid="workspace-directory-tree">
      {header}
      {refreshError !== null && (
        <p className="flex-shrink-0 px-2 pt-1 text-caption text-destructive" role="alert">
          Refresh failed: {refreshError}
        </p>
      )}
      {actionError != null && actionError !== '' && (
        <p
          className="flex-shrink-0 px-2 pt-1 text-caption text-destructive"
          role="alert"
          data-testid="workspace-node-action-error"
        >
          Action failed: {actionError}
        </p>
      )}
      {notReady ? (
        <div className="flex flex-1 items-center justify-center p-4">
          <p
            data-testid="workspace-files-not-ready"
            className={cn(
              'text-body',
              notReady.tone === 'error' && 'text-destructive',
              notReady.tone === 'warning' && 'text-warning-text',
              notReady.tone === 'muted' && 'text-muted-foreground'
            )}
          >
            {notReady.message}
            {notReady.action && (
              <>
                {' '}
                <Link
                  to={notReady.action.href}
                  className="underline"
                  data-testid="workspace-files-open-workstation"
                >
                  {notReady.action.label}
                </Link>
              </>
            )}
            {notReady.canRetry && (
              <>
                {' '}
                <button className="underline" onClick={() => void resume()}>retry</button>
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <FileTree
            entries={rootEntries ?? []}
            loadChildren={loadChildren}
            selectedPath={activePath}
            onSelect={(entry) => onOpenFile(entry.path)}
            onNodeDragStart={onNodeDragStart}
            onNodeContextMenu={onNodeContextMenu}
            onBackgroundContextMenu={onBackgroundContextMenu}
            refreshRef={refreshRef}
          />
        </div>
      )}
    </div>
  );
}
